import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  ActionConfirmData,
  ActionConfirmDialogComponent,
  ActionConfirmResult,
} from '../pages/abuse/action-confirm-dialog/action-confirm-dialog.component';
import { UsersService } from './users-service.service';

/**
 * Block and unblock, with the confirmation in front, from anywhere.
 *
 * The case page was the only place an admin could block, and it had the whole
 * ceremony: fetch the on-hold email, show it, let it be edited, then send.
 * The profile dialog needed the same - a spammer's profile text is often the
 * first thing that gives them away, before the guard has anything to count -
 * and copying the ceremony would have meant two places to keep the wording,
 * the email review and the reason field in step. So it lives here once.
 *
 * Each call resolves to true when the action ran, false when the admin
 * cancelled, and errors when either the preview or the action itself failed.
 * Whatever loaded the account decides what to show for each of those.
 */
@Injectable({ providedIn: 'root' })
export class AccountBlockService {
  constructor(private usersService: UsersService, private dialog: MatDialog) {}

  block(uid: string): Observable<boolean> {
    return this.usersService.getBlockEmailPreview(uid).pipe(
      catchError((err) =>
        throwError(() =>
          new Error(
            err?.status === 404
              ? 'This account has no sign-in record in this environment, so there is ' +
                'nothing to block. On QA the member rows are a copy of prod but the ' +
                'sign-ins are not; try it on prod.'
              : `Could not load the email preview: ${err?.message ?? err}`
          )
        )
      ),
      switchMap((preview) =>
        this.confirm({
          title: 'Block this account?',
          description:
            'Disables sign-in immediately, revokes any session already open, and ' +
            'adds this name to the persona watchlist so a repeat under a different ' +
            'account is caught faster.',
          confirmLabel: 'Block account',
          danger: true,
          emailPreview: preview,
          reason: {
            label: 'Reason (kept with the case, not sent)',
            hint: 'What you saw — e.g. "profile text is a romance-scam opener; 23 chats, 0 replies".',
          },
        })
      ),
      switchMap((result) => {
        if (!result) return of(false);
        return this.usersService
          .blockUser(uid, { email: result.email, reason: result.reason })
          .pipe(map(() => true));
      })
    );
  }

  unblock(uid: string): Observable<boolean> {
    return this.confirm({
      title: 'Restore this account?',
      description:
        "Re-enables sign-in, clears the guard's tracking window so the account does " +
        'not immediately re-trip the same threshold that banned it, and removes its ' +
        'name from the persona watchlist. No email is sent by this action — the ' +
        'person will simply find they can sign in again next time they try.',
      confirmLabel: 'Unblock account',
    }).pipe(
      switchMap((result) => {
        if (!result) return of(false);
        return this.usersService.unblockUser(uid).pipe(map(() => true));
      })
    );
  }

  /** The shared confirm dialog; undefined when it was dismissed. */
  private confirm(data: ActionConfirmData): Observable<ActionConfirmResult | undefined> {
    return this.dialog
      .open<ActionConfirmDialogComponent, ActionConfirmData, ActionConfirmResult>(
        ActionConfirmDialogComponent,
        { data, autoFocus: false }
      )
      .afterClosed()
      .pipe(map((result) => (result?.confirmed ? result : undefined)));
  }
}
